// El VUELO de un producto elegido hasta el pie (ADR-2026-10-14-el-pie-de-elegir-productos-es-el-del-agente):
// al tocar un producto en la grilla, una copia de su foto viaja hasta su miniatura en la barra
// de abajo, como lo adjuntado viaja a la caja en el selector del agente (`vueloAlComposer`). La
// tarjeta se queda en la grilla, marcada: lo que se mueve es la copia.
//
// La miniatura de destino se mide dos frames después (entra con su propia animación y la fila
// se reacomoda al pintar) y queda invisible mientras la copia viaja; al aterrizar se la muestra,
// se borra la copia (así no hay un frame vacío) y la miniatura REBOTA, como algo que cae en la
// barra. Con menos movimiento pedido no vuela ni rebota nada.

const prefiereMenosMovimiento = (): boolean => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

/** Lanza el vuelo de `id` desde su tarjeta en `grilla` hasta su miniatura en `pie`. Devuelve
 *  cómo cancelarlo (si sale otro antes, o la pantalla se va). */
export function volarAlPie(id: string, grilla: HTMLElement | null, pie: HTMLElement | null, esperaMs = 0): () => void {
  if (!grilla || !pie || prefiereMenosMovimiento()) return () => {};
  const tarjeta = grilla.querySelector<HTMLElement>(`[data-tira-tile="${CSS.escape(id)}"]`);
  const foto = tarjeta?.querySelector<HTMLImageElement>('img');
  if (!tarjeta || !foto?.src) return () => {};
  const origen = foto.getBoundingClientRect();
  if (origen.width === 0) return () => {};

  const copia = document.createElement('div');
  copia.setAttribute('aria-hidden', 'true');
  Object.assign(copia.style, {
    position: 'fixed',
    left: '0px',
    top: '0px',
    width: `${origen.width}px`,
    height: `${origen.height}px`,
    borderRadius: '12px',
    overflow: 'hidden',
    zIndex: '300',
    pointerEvents: 'none',
    boxShadow: '0 10px 30px -10px rgb(var(--brand-black-rgb) / 0.35)',
    transformOrigin: '0 0',
    transform: `translate(${origen.left}px, ${origen.top}px)`,
  } satisfies Partial<CSSStyleDeclaration>);
  const img = document.createElement('img');
  img.src = foto.currentSrc || foto.src;
  Object.assign(img.style, { width: '100%', height: '100%', objectFit: 'cover', display: 'block' });
  copia.appendChild(img);
  document.body.appendChild(copia);

  let destino: HTMLElement | null = null;
  let anim: Animation | null = null;
  const mostrarDestino = () => {
    if (!destino) return;
    destino.style.transition = 'none';
    destino.style.opacity = '';
    void destino.offsetHeight;
    destino.style.transition = '';
  };
  const aterrizar = () => {
    mostrarDestino();
    copia.remove();
    destino?.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.22)', offset: 0.3 },
        { transform: 'scale(0.92)', offset: 0.55 },
        { transform: 'scale(1.06)', offset: 0.78 },
        { transform: 'scale(1)' },
      ],
      { duration: 460, easing: 'ease-out' },
    );
  };
  const arrancar = () => {
    destino = pie.querySelector<HTMLElement>(`[data-miniatura-elegida="${CSS.escape(id)}"]`);
    if (!destino) {
      copia.remove();
      return;
    }
    destino.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    destino.style.opacity = '0';
    const hasta = destino.getBoundingClientRect();
    const escala = hasta.width / origen.width;
    anim = copia.animate(
      [
        { transform: `translate(${origen.left}px, ${origen.top}px) scale(1)`, borderRadius: '12px' },
        { transform: `translate(${hasta.left}px, ${hasta.top}px) scale(${escala})`, borderRadius: `${10 / escala}px` },
      ],
      { duration: 420, easing: 'cubic-bezier(.2,.7,.25,1)', fill: 'forwards' },
    );
    anim.onfinish = aterrizar;
    anim.oncancel = () => {
      mostrarDestino();
      copia.remove();
    };
  };
  // `esperaMs`: la barra está APARECIENDO (es el primer producto) y se mueve mientras entra; la
  // copia espera quieta en el origen a que termine, si no aterriza donde la barra todavía no está.
  let segundo = 0;
  let primero = 0;
  const espera = window.setTimeout(() => {
    primero = requestAnimationFrame(() => {
      segundo = requestAnimationFrame(arrancar);
    });
  }, esperaMs);
  return () => {
    window.clearTimeout(espera);
    cancelAnimationFrame(primero);
    cancelAnimationFrame(segundo);
    if (anim) {
      anim.onfinish = null;
      anim.oncancel = null;
      anim.cancel();
    }
    mostrarDestino();
    copia.remove();
  };
}
